import { ResultOf } from 'gql.tada';
import { gql } from 'graphql-request';

import { makeGraphQlCall } from '../../data/util';
import {
  BackEndCodeLanguageType,
  BuildSystemType,
  DefaultTransformationEntityType,
  ExternalGitProviderType,
  ForkModeType,
  SyncModeType,
  VersionControlMode,
  VersionControlModeType
} from '../../graphqlTypes/enums';
import { graphql } from '../../graphqlTypes/graphql';
import { userGitCredsQuery } from '../git/GitIntegration/queries';
import { getAspectValue } from '../utils/misc/aspect';
import { Aspect, DetailTypeWithProject, VersionedAspect } from './common';
import { ProjectContentCountFields, ProjectSqlContentCountFields } from './ProjectContentFields';

export type ProjectDetail = {
  _id: string;
  name: string;
  language: BackEndCodeLanguageType;
  providerType?: string;
  pipelinesCount: number;
  defaultTransformationEntity: DefaultTransformationEntityType;
  created: number;
  createdBy: string;
  mainBranch: string;
  forkMode: ForkModeType;
  syncMode: SyncModeType;
  versionControlMode: VersionControlModeType;
  team: { _id: string; name: string };
  latestAttemptedReleaseVersion?: string;
  publishedToPackageHub: boolean;
  clonedReleaseVersion?: string;
};

export type Template = {
  _id: string;
  versionedAspects: VersionedAspect[];
};

export const projectDetailQuery = graphql(`
  query ProjectDetailQuery($projectId: String!) {
    Project(uid: $projectId) {
      _id
      name
      hasAnalysis
      isExampleProject
      team {
        _id
        name
      }
      pipelinesCount
      defaultTransformationEntity
      language
      providerType
      created
      createdBy
      mainBranch
      forkMode
      syncMode
      latestAttemptedReleaseVersion
      clonedReleaseVersion
      publishedToPackageHub
      versionControlMode
    }
  }
`);
export type ProjectDetailQuery = ResultOf<typeof projectDetailQuery>;

export enum GitTagState {
  ExternalAndInternallyReleased = 'ExternalAndInternallyReleased',
  InternalReleased = 'InternalReleased',
  ExternalUnReleased = 'ExternalUnReleased'
}

export const projectReleasesQuery = graphql(`
  query ProjectReleaseList($projectId: String, $statuses: [ReleaseStatus!]!) {
    ProjectReleaseByProjectIdAndStatus(optionalProjectUID: $projectId, statuses: $statuses) {
      releaseTag
      createdAt
      createdByFullName
      tagMessage
      status
    }
  }
`);
export const SqlProjectAboutQuery = graphql(`
    query SqlProjectAboutQuery($projectId: String!) {
      Project(uid: $projectId) {
        _id
        description
        readme
        team {
          _id
          name
        }
        providerType
        created
        createdByFullName
        forkMode
        latestAttemptedReleaseVersion
        clonedReleaseVersion
        currentProjectStatus
        ${ProjectSqlContentCountFields}
      }
    }
  `);
export type SqlProjectAboutQueryResponse = ResultOf<typeof SqlProjectAboutQuery>;
export const ProjectAboutQuery = graphql(`
    query ProjectAboutQuery($projectId: String!) {
      Project(uid: $projectId) {
        _id
        description
        readme
        team {
          _id
          name
        }
        providerType
        created
        createdByFullName
        forkMode
        latestAttemptedReleaseVersion
        clonedReleaseVersion
        ${ProjectContentCountFields}
      }
    }
  `);
export type ProjectAboutQueryResponse = ResultOf<typeof ProjectAboutQuery>;

export const PackageLatestReleaseQuery = graphql(`
  query PackageAboutQuery($projectId: String!) {
    ProjectReleaseSnapshot(uid: $projectId) {
      _id
      latestAttemptedReleaseVersion
    }
  }
`);

type DeploymentSettings = {
  isAutoDeployEnabled: boolean;
  isSelectiveJobDeploymentEnabled: boolean;
  isUTEnabled: boolean;
};

export type ProjectSettings<T extends boolean> = {
  deploymentSettings?: DeploymentSettings;
} & (T extends false
  ? {
      artefactID: string;
      parentArtefactID: string;
      changelogFile: string;
      buildSystemTemplatesEnabled?: boolean;
      defaultTemplate: string;
      templates: Template[];
    }
  : T extends true
    ? {}
    : never);

export const fetchProjectSettings =
  <T extends boolean>(onlyDeploymentSettings: T) =>
  async (variables: { projectId: string; branch?: string }) => {
    const advancedSettingsQuery = `
      changelogFile
      buildSystemTemplatesEnabled
      defaultTemplate
      artefactID
      parentArtefactID
      templates {
        _id
        versionedAspects(branch: $branch, aspectVers: [{ aspect: Configuration }]) {
          VersionedAspectName
          VersionedAspectValue
        }
      }
    `;

    const query = gql`
      query ProjectAdvanceTabQuery($projectId: String!${onlyDeploymentSettings ? '' : `, $branch: String`}) {
        Project(uid: $projectId) {
          ${onlyDeploymentSettings ? '' : advancedSettingsQuery}
          DeploymentSettingsAspect: aspects(aspect: DeploymentSettings) {
            AspectValue
            AspectName
          }
        }
      }
    `;

    const response = (await makeGraphQlCall(query, variables)) as {
      Project: {
        DeploymentSettingsAspect: Aspect<DeploymentSettings>[];
      };
    };

    const {
      Project: { DeploymentSettingsAspect, ...output }
    } = response;

    const deploymentSettings = getAspectValue(DeploymentSettingsAspect);

    return { ...output, deploymentSettings } as ProjectSettings<T>;
  };

export const projectTemplatesQuery = graphql(`
  query projectTemplatesQuery($projectId: String!, $branch: String) {
    Project(uid: $projectId) {
      defaultTemplate
      templates {
        _id
        versionedAspects(branch: $branch, aspectVers: [{ aspect: Configuration }]) {
          VersionedAspectName
          VersionedAspectValue
        }
      }
    }
  }
`);
export const ProjectGitStatusQuery = graphql(`
  query ProjectGitStatusQuery($projectId: String!) {
    Project(uid: $projectId) {
      forkMode
      syncMode
      versionControlMode
      currentProjectStatus
      externalOriginUri
      externalSyncStatus {
        url
        state
        displayState
        displayMessage
      }
      savedGitCredsStatus {
        ${userGitCredsQuery}
      }
      
    }
  }
`);
export type UserGitCreds = NonNullable<
  NonNullable<NonNullable<ResultOf<typeof ProjectGitStatusQuery>>['Project']>['savedGitCredsStatus']
>;
export const gitCodeQuery = graphql(`
  query gitCodeQuery($projectId: String!) {
    Project(uid: $projectId) {
      syncMode
      workingGitContext {
        currentUrl
      }
    }
  }
`);

export const subscriberTeamsQuery = graphql(`
  query subscriberTeamsQuery($projectId: String!) {
    Project(uid: $projectId) {
      name
      subscriberTeams {
        name
        subscribedProjectID
        isAuthor
        _id
      }
    }
  }
`);

export const packageSubscriberTeamsQuery = graphql(`
  query packageSubscriberTeamsQuery($projectId: String!) {
    ProjectReleaseSnapshot(uid: $projectId) {
      subscriberTeamsValidForUser {
        _id
      }
    }
  }
`);
//  FIXME: remove once gql schema types are working
export type PackageSubscriberTeamsQueryResponse = {
  ProjectReleaseSnapshot: {
    subscriberTeamsValidForUser: { _id: string }[];
  };
};
export const subscribedDatasetsQuery = graphql(`
  query subscribedDatasetsQuery($projectId: String!) {
    Project(uid: $projectId) {
      subscribedDatasets {
        _id
        name
        project {
          _id
          name
        }
      }
    }
  }
`);

export type ProjectType = DetailTypeWithProject & {
  _id: string;
  name: string;
  mainBranch: string;
  mainBranchModificationAllowed: boolean;
  language: BackEndCodeLanguageType;
  providerType?: string;
};

export type UserProjectsQueryType = {
  User: {
    projects: ProjectType[];
  };
};

export const ProjectDropdownOptionsQuery = (ownerEntity: string, childEntity: string, aspectQuery: string) => gql`
  query ProjectDropdownOptionsQuery($entityId: String!) {
    ${ownerEntity}(uid: $entityId) {
      _id
      ${childEntity} {
        _id
        name
        mainBranch
        mainBranchModificationAllowed
        language
        ${aspectQuery}
      }
    }
  }
`;

export type ProjectEntityVariables = {
  name: string;
  ownerUID: string;
  mainBranch: string;
  forkMode: ForkModeType;
  language: BackEndCodeLanguageType;
  backendLanguage?: BackEndCodeLanguageType;
  dependencyProjectUIDs?: string[];
  externalUriArg?: string;
  externalSubPath?: string;
  externalRepoEmail?: string;
  externalRepoUsername?: string;
  versionControlMode: VersionControlModeType;
  externalRepoAccessToken?: string;
  externalGitProvider?: ExternalGitProviderType;
  userGitCredsUID?: number;
  isImport?: boolean;
  defaultDevelopmentBranch?: string;
  defaultTransformationEntity?: DefaultTransformationEntityType;
};

export const createProjectWithExternalGitEntityMutationQuery = graphql(`
  mutation createProjectEntity(
    $name: String!
    $ownerUID: String!
    $mainBranch: String!
    $language: BackEndCodeLanguage
    $backendLanguage: BackEndCodeLanguage
    $forkMode: ForkMode!
    $dependencyProjectUIDs: [String!]
    $externalUriArg: String
    $externalSubPath: String
    $externalRepoEmail: String
    $externalRepoUsername: String
    $externalRepoAccessToken: String
    $externalGitProvider: ExternalGitProviderType
    $userGitCredsUID: Long
    $description: String
    $providerType: String
    $versionControlMode: VersionControlMode!
    $isImport: Boolean
    $defaultDevelopmentBranch: String
    $defaultTransformationEntity: DefaultTransformationEntity
  ) {
    addProject(
      name: $name
      ownerUID: $ownerUID
      mainBranch: $mainBranch
      language: $language
      backendLanguage: $backendLanguage
      forkMode: $forkMode
      dependencyProjectUIDs: $dependencyProjectUIDs
      externalUriArg: $externalUriArg
      externalSubPath: $externalSubPath
      externalRepoEmail: $externalRepoEmail
      externalRepoUsername: $externalRepoUsername
      externalRepoAccessToken: $externalRepoAccessToken
      userGitCredsUID: $userGitCredsUID
      externalGitProvider: $externalGitProvider
      description: $description
      providerType: $providerType
      versionControlMode: $versionControlMode
      isImport: $isImport
      defaultDevelopmentBranch: $defaultDevelopmentBranch
      defaultTransformationEntity: $defaultTransformationEntity
    ) {
      _id
      name
    }
  }
`);

export const createProjectWithoutExternalGitEntityMutationQuery = graphql(`
  mutation createProjectEntity(
    $name: String!
    $ownerUID: String!
    $mainBranch: String!
    $language: BackEndCodeLanguage
    $backendLanguage: BackEndCodeLanguage
    $forkMode: ForkMode!
    $dependencyProjectUIDs: [String!]
    $userGitCredsUID: Long
    $description: String
    $providerType: String
    $versionControlMode: VersionControlMode!
    $isImport: Boolean
    $defaultTransformationEntity: DefaultTransformationEntity
  ) {
    addProject(
      name: $name
      ownerUID: $ownerUID
      mainBranch: $mainBranch
      language: $language
      backendLanguage: $backendLanguage
      forkMode: $forkMode
      dependencyProjectUIDs: $dependencyProjectUIDs
      userGitCredsUID: $userGitCredsUID
      description: $description
      providerType: $providerType
      versionControlMode: $versionControlMode
      isImport: $isImport
      defaultTransformationEntity: $defaultTransformationEntity
    ) {
      _id
      name
    }
  }
`);

export const unlinkExternalGitMutation = graphql(`
  mutation unlinkExternalGit($projectUID: String!) {
    unlinkExternalGit(projectUID: $projectUID)
  }
`);
export const linkInternalGitMutation = graphql(`
  mutation linkInternalGit($projectUID: String!) {
    linkInternalGit(projectUID: $projectUID)
  }
`);

export const linkExternalGitMutation = graphql(`
  mutation linkExternalGit(
    $projectUID: String!
    $externalRepoUri: String
    $externalRepoEmail: String
    $externalRepoUsername: String
    $externalRepoAccessToken: String
    $userGitCredsUID: Long
    $externalGitProvider: ExternalGitProviderType
  ) {
    linkExternalGit(
      projectUID: $projectUID
      externalRepoUri: $externalRepoUri
      externalRepoEmail: $externalRepoEmail
      externalRepoUsername: $externalRepoUsername
      externalRepoAccessToken: $externalRepoAccessToken
      userGitCredsUID: $userGitCredsUID
      externalGitProvider: $externalGitProvider
    )
  }
`);

export const linkSavedCredsToExternalGitMutation = graphql(`
  mutation linkSavedCredsToExternalGit(
    $projectUID: String!
    $externalRepoEmail: String
    $externalRepoUsername: String
    $externalRepoAccessToken: String
    $userGitCredsUID: Long
    $externalGitProvider: ExternalGitProviderType
  ) {
    linkSavedCredsToExternalGit(
      projectUID: $projectUID
      externalRepoEmail: $externalRepoEmail
      externalRepoUsername: $externalRepoUsername
      externalRepoAccessToken: $externalRepoAccessToken
      userGitCredsUID: $userGitCredsUID
      externalGitProvider: $externalGitProvider
    )
  }
`);

export const updateProject = graphql(`
  mutation UpdateProject(
    $uid: String!
    $projectDependency: String
    $buildSystemTemplatesEnabled: Boolean
    $changelogFile: String
    $projectDeploymentSettings: String
    $publishedProphecyPackage: Boolean
  ) {
    updateProject(
      uid: $uid
      projectDependency: $projectDependency
      buildSystemTemplatesEnabled: $buildSystemTemplatesEnabled
      changelogFile: $changelogFile
      projectDeploymentSettings: $projectDeploymentSettings
      publishedProphecyPackage: $publishedProphecyPackage
    ) {
      _id
    }
  }
`);

export const addTemplateQuery = graphql(`
  mutation AddTemplate($uid: String!, $name: String!) {
    addTemplate(ownerUID: $uid, name: $name) {
      _id
    }
  }
`);

export const UpdateGitTokenMutation = graphql(`
  mutation UpdateGitToken($uid: String!, $externalRepoAccessToken: String!) {
    updateGitToken(uid: $uid, externalRepoAccessToken: $externalRepoAccessToken)
  }
`);
export type CloneProjectVariable = {
  uid: string;
  name: string;
  teamUID: string;
  externalSubPath?: string;
  externalUriArg?: string;
  externalRepoUsername?: string;
  externalRepoEmail?: string;
  externalRepoAccessToken?: string;
  copyMainBranchReleaseTags: boolean;
  commitTemplate?: string;
  userGitCredsUID?: number;
  externalGitProvider?: ExternalGitProviderType;
  versionControlMode?: VersionControlMode;
  defaultDevelopmentBranch?: string;
};

export type ExternalGitCloneProjectVariable = {
  uid: string;
  name: string;
  teamUID: string;
  externalSubPath?: string;
  externalUriArg?: string;
  externalRepoUsername?: string;
  externalRepoEmail?: string;
  externalRepoAccessToken?: string;
  copyMainBranchReleaseTags: boolean;
  commitTemplate?: string;
  userGitCredsUID?: number;
  externalGitProvider?: ExternalGitProviderType;
  versionControlMode?: VersionControlMode;
  defaultDevelopmentBranch?: string;
  defaultMainBranch?: string;
};

export type CloneProjectResponse = {
  cloneProject: {
    _id: string;
    name: string;
  };
};

export type MigrateToExternalGitResponse = {
  migrateToExternalGit: {
    _id: string;
    name: string;
  };
};
export const cloneProjectMutation = graphql(`
  mutation cloneProject(
    $uid: String!
    $name: String!
    $teamUID: String!
    $externalSubPath: String
    $externalUriArg: String
    $externalRepoUsername: String
    $externalRepoEmail: String
    $externalRepoAccessToken: String
    $copyMainBranchReleaseTags: Boolean
    $userGitCredsUID: Long
    $externalGitProvider: ExternalGitProviderType
    $versionControlMode: VersionControlMode
    $defaultDevelopmentBranch: String
    $commitTemplate: String
  ) {
    cloneProject(
      uid: $uid
      name: $name
      teamUID: $teamUID
      externalSubPath: $externalSubPath
      externalUriArg: $externalUriArg
      externalRepoUsername: $externalRepoUsername
      externalRepoEmail: $externalRepoEmail
      externalRepoAccessToken: $externalRepoAccessToken
      copyMainBranchReleaseTags: $copyMainBranchReleaseTags
      userGitCredsUID: $userGitCredsUID
      externalGitProvider: $externalGitProvider
      versionControlMode: $versionControlMode
      defaultDevelopmentBranch: $defaultDevelopmentBranch
      commitTemplate: $commitTemplate
    ) {
      _id
      name
    }
  }
`);

export const migrateToExternalGitMutation = graphql(`
  mutation migrateToExternalGit(
    $uid: String!
    $externalSubPath: String
    $externalUriArg: String
    $externalRepoUsername: String
    $externalRepoEmail: String
    $externalRepoAccessToken: String
    $copyMainBranchReleaseTags: Boolean
    $userGitCredsUID: Long
    $externalGitProvider: ExternalGitProviderType
    $defaultMainBranch: String!
    $defaultDevelopmentBranch: String!
  ) {
    migrateToExternalGit(
      uid: $uid
      externalSubPath: $externalSubPath
      externalUriArg: $externalUriArg
      externalRepoEmail: $externalRepoEmail
      externalRepoUsername: $externalRepoUsername
      externalRepoAccessToken: $externalRepoAccessToken
      copyMainBranchReleaseTags: $copyMainBranchReleaseTags
      userGitCredsUID: $userGitCredsUID
      externalGitProvider: $externalGitProvider
      defaultMainBranch: $defaultMainBranch
      defaultDevelopmentBranch: $defaultDevelopmentBranch
    ) {
      _id
      name
    }
  }
`);

export type DependencyConflictType = {
  conflictingReleaseVersionsSummary: string[];
  project: {
    name: string;
  };
};

export const ClusterProjectCreationDefaultsQuery = graphql(`
  query ClusterProjectCreationDefaultsQuery {
    ClusterProjectCreationDefaults {
      language
      gitProviderType
      sqlProviderType
      versionControlType
      gitStorageModel
      defaultMainBranch
      defaultDevelopmentBranch
      gitStorageModel
    }
  }
`);

export type ClusterProjectCreationDefaultsQueryResponse = ResultOf<typeof ClusterProjectCreationDefaultsQuery>;

export const UpdateClusterProjectCreationDefaultsMutation = graphql(`
  mutation UpdateClusterProjectCreationDefaults(
    $language: BackEndCodeLanguage!
    $gitProviderType: GitProviderType!
    $versionControlMode: VersionControlMode
    $forkMode: ForkMode
    $defaultMainBranch: String
    $defaultDevelopmentBranch: String
    $sqlProviderType: String
  ) {
    updateClusterProjectCreationDefaults(
      language: $language
      gitProviderType: $gitProviderType
      versionControlMode: $versionControlMode
      forkMode: $forkMode
      defaultMainBranch: $defaultMainBranch
      defaultDevelopmentBranch: $defaultDevelopmentBranch
      sqlProviderType: $sqlProviderType
    )
  }
`);

export const ProjectDependenciesQuery = graphql(`
  query ProjectDependenciesQuery($projectId: String!) {
    Project(uid: $projectId) {
      latestReleaseVersion
      team {
        _id
      }
      language
      dependencies
      projectDependencyInfo {
        dependencies {
          version
          directDependency
          project {
            _id
            name
            description
            created
            latestReleaseVersion
            team {
              name
            }
          }
        }
        dependencyConflicts {
          project {
            name
          }
          conflictingReleaseVersionsSummary
        }
      }
    }
  }
`);

export type ProjectDependenciesQueryResponse = ResultOf<typeof ProjectDependenciesQuery>;

export const TeamProjectCreationDefaultsQuery = graphql(`
  query TeamProjectCreationDefaultsQuery($teamUID: String!) {
    TeamProjectCreationDefaults(teamUID: $teamUID) {
      language
      gitProviderType
      sqlProviderType
      versionControlType
      gitStorageModel
      defaultMainBranch
      defaultDevelopmentBranch
    }
  }
`);

export type TeamProjectCreationDefaultsQueryResponse = ResultOf<typeof TeamProjectCreationDefaultsQuery>;

export const UpdateTeamProjectCreationDefaultsMutation = graphql(`
  mutation UpdateTeamProjectCreationDefaults(
    $language: BackEndCodeLanguage!
    $gitProviderType: GitProviderType!
    $versionControlMode: VersionControlMode
    $forkMode: ForkMode
    $defaultMainBranch: String
    $defaultDevelopmentBranch: String
    $sqlProviderType: String
    $teamUID: String!
  ) {
    updateTeamProjectCreationDefaults(
      language: $language
      gitProviderType: $gitProviderType
      versionControlMode: $versionControlMode
      forkMode: $forkMode
      defaultMainBranch: $defaultMainBranch
      defaultDevelopmentBranch: $defaultDevelopmentBranch
      sqlProviderType: $sqlProviderType
      teamUID: $teamUID
    )
  }
`);

export const addOrUpdateCopilotLLMConfigMutation = graphql(`
  mutation addOrUpdateCopilotLLMConfig(
    $AIModelProvidersCreds: String!
    $availableAIModels: String!
    $availableAISpeechModels: String!
  ) {
    addOrUpdateCopilotLLMConfig(
      AIModelProvidersCreds: $AIModelProvidersCreds
      availableAIModels: $availableAIModels
      availableAISpeechModels: $availableAISpeechModels
    )
  }
`);

export const getCopilotLLMConfigQuery = graphql(`
  query getCopilotLLMConfig {
    getCopilotLLMConfig {
      AIModelProvidersCreds
      availableAIModels
      availableAISpeechModels
    }
  }
`);

export const getOrchestrationSettingsQuery = graphql(`
  query getOrchestrationSettings {
    OrchestrationEndpoints {
      uid
      endpoint
      default
      masterKey {
        keyType
        keyValue
      }
    }
  }
`);
export type GetOrchestrationSettingsQueryResponse = ResultOf<typeof getOrchestrationSettingsQuery>;

export const addOrchestrationEndpointMutation = graphql(`
  mutation addOrchestrationEndpoint(
    $endpointUrl: String!
    $default: Boolean!
    $masterKeyType: String!
    $masterKeyValue: String!
  ) {
    addOrchestrationEndpoint(
      endpointUrl: $endpointUrl
      default: $default
      masterKeyType: $masterKeyType
      masterKeyValue: $masterKeyValue
    )
  }
`);

export const updateOrchestrationEndpointMutation = graphql(`
  mutation updateOrchestrationEndpoint(
    $uid: String!
    $endpointUrl: String!
    $default: Boolean!
    $masterKeyType: String!
    $masterKeyValue: String!
  ) {
    updateOrchestrationEndpoint(
      uid: $uid
      endpointUrl: $endpointUrl
      default: $default
      masterKeyType: $masterKeyType
      masterKeyValue: $masterKeyValue
    )
  }
`);

export const removeOrchestrationEndpointMutation = graphql(`
  mutation removeOrchestrationEndpoint($uid: String!) {
    deleteOrchestrationEndpoint(uid: $uid)
  }
`);

export const removeProjectDependencyMutation = graphql(`
  mutation removeProjectDependencyMutation($projectId: String!, $dependencyProjectUID: String!) {
    removeProjectDependency(uid: $projectId, dependencyProjectUID: $dependencyProjectUID) {
      _id
      name
    }
  }
`);

export const addDefaultDependenciesMutation = graphql(`
  mutation addDefaultDependenciesMutation($teamUID: String!, $projectUIDs: [Long!]!) {
    addDefaultDependencies(teamUID: $teamUID, ProjectUIDs: $projectUIDs)
  }
`);

export const setAnalysisEnabledMutation = graphql(`
  mutation setAnalysisEnabled($projectUID: String!, $hasAnalysis: Boolean!) {
    setAnalysisEnabled(projectUID: $projectUID, hasAnalysis: $hasAnalysis)
  }
`);

export const removeDefaultDependenciesMutation = graphql(`
  mutation removeDefaultDependenciesMutation($teamUID: String!, $projectUIDs: [Long!]!) {
    removeDefaultDependencies(teamUID: $teamUID, ProjectUIDs: $projectUIDs)
  }
`);

export const addProjectDependencyMutation = graphql(`
  mutation addProjectDependencyMutation($projectId: String!, $dependencyProjectUID: String!, $releaseTag: String) {
    addProjectDependency(uid: $projectId, dependencyProjectUID: $dependencyProjectUID, releaseTag: $releaseTag) {
      _id
      name
    }
  }
`);

export const updateProjectDependencyMutation = graphql(`
  mutation updateProjectDependency($projectId: String!, $dependencyProjectUID: String!, $releaseTag: String!) {
    updateProjectDependency(uid: $projectId, dependencyProjectUID: $dependencyProjectUID, releaseTag: $releaseTag) {
      _id
      name
    }
  }
`);

export const updateProjectDependenciesMutation = graphql(`
  mutation updateProjectAllDependencies($projectId: String!) {
    updateProjectAllDependencies(uid: $projectId) {
      _id
      message
    }
  }
`);

export const prCreationTemplateQuery = graphql(`
  query getPrCreationTemplate($projectId: String!) {
    getPrCreationTemplate(projectUID: $projectId) {
      isPrCreationEnabled
      prTemplate
    }
  }
`);

export const prCreationTemplateMutation = graphql(`
  mutation setPrCreationTemplate($projectId: String!, $prCreationEnabled: Boolean!, $customPrTemplate: String) {
    setPrCreationTemplate(
      projectUID: $projectId
      prCreationEnabled: $prCreationEnabled
      customPrTemplate: $customPrTemplate
    )
  }
`);

export const prCreationURLQuery = graphql(`
  query prCreationRedirectUrl($projectId: String!, $from: String!, $to: String!) {
    prCreationRedirectUrl(projectUID: $projectId, from: $from, to: $to) {
      isPrCreationEnabled
      externalPrUrlRedirect
    }
  }
`);

export const updateTemplateMutation = graphql(`
  mutation updateTemplateVersionMutation($uid: String!) {
    updateTemplateVersion(uid: $uid) {
      name
    }
  }
`);

export const updateArtefactIDMutation = graphql(`
  mutation updateArtefactIDMutation($uid: String!, $entity: EntityKind!, $artefactID: String!) {
    updateArtefactID(uid: $uid, entityKind: $entity, artefactID: $artefactID)
  }
`);

export const updateParentArtefactIDForProjectMutation = graphql(`
  mutation updateParentArtefactIDForProjectMutation($uid: String!, $artefactID: String!) {
    updateParentArtefactIDForProject(uid: $uid, artefactID: $artefactID)
  }
`);

export const mergedExternallyMutation = graphql(`
  mutation mergedExternally($projectUID: String!, $from: String!, $to: String!) {
    mergedExternally(projectUID: $projectUID, from: $from, to: $to) {
      forkMode
    }
  }
`);

export type ExternalProjectDetails = {
  isValidProphecyProject: boolean;
  isValidDbtProject: boolean;
  name: string;
  description: string;
  language: BackEndCodeLanguageType;
  buildSystem: BuildSystemType;
  versionControlMode: VersionControlModeType;
  isSubpathCreationAllowedSimple: boolean;
  existingProjectsMessage: string;
};

export const externalProjectDetailsQuery = graphql(`
  query externalProjectDetails(
    $externalUriArg: String!
    $branch: String!
    $externalSubPath: String!
    $externalRepoEmail: String
    $externalRepoUsername: String
    $externalRepoAccessToken: String
    $userGitCredsUID: Long
  ) {
    externalProjectDetails(
      externalUriArg: $externalUriArg
      externalRepoEmail: $externalRepoEmail
      branch: $branch
      externalRepoUsername: $externalRepoUsername
      externalRepoAccessToken: $externalRepoAccessToken
      externalSubPath: $externalSubPath
      userGitCredsUID: $userGitCredsUID
    ) {
      isValidProphecyProject
      isValidDbtProject
      name
      description
      language
      buildSystem
      existingProjectsMessage
      isSubpathCreationAllowedSimple
    }
  }
`);

export const UpdateProjectReadmeMutation = graphql(`
  mutation UpdateProjectReadmeMutation($projectUID: String!, $content: String!) {
    updateReadme(projectUID: $projectUID, content: $content)
  }
`);

export const releaseFromAnExistingTag = async (variables: { projectUID: string; fromReleaseTag: string }) => {
  const query = graphql(`
    mutation ReleaseFromAnExistingTag($projectUID: String!, $fromReleaseTag: String!) {
      releaseFromAnExistingTag(projectUID: $projectUID, fromReleaseTag: $fromReleaseTag)
    }
  `);

  return makeGraphQlCall(query, variables);
};

export const addOrUpdateTeamProjectCreationTemplateMutation = graphql(`
  mutation addOrUpdateTeamProjectCreationTemplate(
    $uid: String
    $teamUID: String!
    $name: String!
    $isDefault: Boolean!
    $allowToCustomize: Boolean!
    $language: BackEndCodeLanguage!
    $gitProviderType: GitProviderType!
    $gitCombinedStorageModel: GitCombinedStorageModel!
    $sqlProviderType: String
    $defaultMainBranch: String
    $defaultDevelopmentBranch: String
    $defaultTransformationEntity: DefaultTransformationEntity
  ) {
    addOrUpdateTeamProjectCreationTemplate(
      uid: $uid
      teamUID: $teamUID
      name: $name
      isDefault: $isDefault
      allowToCustomize: $allowToCustomize
      language: $language
      gitProviderType: $gitProviderType
      gitCombinedStorageModel: $gitCombinedStorageModel
      sqlProviderType: $sqlProviderType
      defaultMainBranch: $defaultMainBranch
      defaultDevelopmentBranch: $defaultDevelopmentBranch
      defaultTransformationEntity: $defaultTransformationEntity
    )
  }
`);

export const teamProjectCreationTemplatesQuery = graphql(`
  query teamProjectCreationTemplates($teamUID: String!) {
    TeamProjectCreationTemplates(teamUID: $teamUID) {
      uid
      teamUID
      name
      isDefault
      allowToCustomize
      language
      gitProviderType
      gitCombinedStorageModel
      sqlProviderType
      defaultMainBranch
      defaultDevelopmentBranch
      defaultTransformationEntity
    }
  }
`);

export const getProjectStatusConfigQuery = graphql(`
  query getProjectStatusConfig {
    getProjectStatusConfig {
      entryName
      colour
      isAdminEntry
      emailEnabled
    }
  }
`);

export type ProjectStatusConfigType = ResultOf<typeof getProjectStatusConfigQuery>;

export const setProjectStatusConfigMutation = graphql(`
  mutation setProjectStatusConfig($projectStatusConfig: [ProjectStatusEntry!]!) {
    setProjectStatusConfig(projectStatusConfig: $projectStatusConfig)
  }
`);

export const getCDMEnabledForProjectsQuery = graphql(`
  query getCDMEnabledForProjects($uids: [String!]!) {
    getCDMEnabledForProjects(uids: $uids) {
      uid
      isCDMEnabled
    }
  }
`);
export const setCDMEnabledForProjectMutation = graphql(`
  mutation setCDMEnabled($uid: String!, $isCDMEnabled: Boolean!) {
    setCDMEnabled(uid: $uid, isCDMEnabled: $isCDMEnabled)
  }
`);
